import { type IntlShape } from 'react-intl';

import { DurationVariant } from '@jsmdg/yoshi';

type ProductTileDurationMessages = {
    activityDurationMaxPrefix: { defaultMessage: string };
    activityDurationDay: { defaultMessage: string };
    activityDurationDays: { defaultMessage: string };
    activityDurationHours: { defaultMessage: string };
    activityDurationMinutes: { defaultMessage: string };
    travelNight: { defaultMessage: string };
    travelNights: { defaultMessage: string };
};

type ProductTileDurationProps = {
    durationLabel?: string;
    durationVariant?: DurationVariant;
};

enum DurationUnit {
    Days = 'days',
    Hours = 'hours',
    Minutes = 'minutes',
}

const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_HOUR = 3_600;

const formatDurationValue = (value: number): string =>
    Number.isInteger(value) ? String(value) : String(value).replace('.', ',');

const normalizeActivityDuration = (
    activityDuration: number | number[] | null | undefined,
): number[] | undefined => {
    if (activityDuration === null || activityDuration === undefined) {
        return undefined;
    }

    if (Array.isArray(activityDuration)) {
        return activityDuration.length ? activityDuration : undefined;
    }

    return [activityDuration];
};

const formatSecondsToDuration = (seconds: number): { value: number; unit: DurationUnit } => {
    if (seconds >= SECONDS_PER_DAY) {
        const days = seconds / SECONDS_PER_DAY;
        const value = Number.isInteger(days) ? days : Math.round(days * 10) / 10;

        return { value, unit: DurationUnit.Days };
    }

    if (seconds >= SECONDS_PER_HOUR) {
        const hours = seconds / SECONDS_PER_HOUR;
        const value = Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10;

        return { value, unit: DurationUnit.Hours };
    }

    return { value: Math.round(seconds / 60), unit: DurationUnit.Minutes };
};

const getActivityDurationLabel = (
    activityDuration: number | number[] | null | undefined,
    intl: IntlShape,
    messages: ProductTileDurationMessages,
): string | undefined => {
    const normalizedActivityDuration = normalizeActivityDuration(activityDuration);

    if (!normalizedActivityDuration) {
        return undefined;
    }

    const maxDuration = Math.max(...normalizedActivityDuration);
    const { value, unit } = formatSecondsToDuration(maxDuration);
    const hasVaryingDurations = new Set(normalizedActivityDuration).size > 1;
    const prefix = hasVaryingDurations
        ? `${intl.formatMessage(messages.activityDurationMaxPrefix)} `
        : '';
    const unitLabel = intl.formatMessage(
        unit === DurationUnit.Days
            ? value === 1
                ? messages.activityDurationDay
                : messages.activityDurationDays
            : unit === DurationUnit.Hours
              ? messages.activityDurationHours
              : messages.activityDurationMinutes,
    );

    return `${prefix}${formatDurationValue(value)} ${unitLabel}`;
};

const getTravelNightsLabel = (
    travelNights: number,
    intl: IntlShape,
    messages: ProductTileDurationMessages,
): string => {
    const unit =
        travelNights === 1
            ? intl.formatMessage(messages.travelNight)
            : intl.formatMessage(messages.travelNights);

    return `${travelNights} ${unit}`;
};

const getProductTileDuration = ({
    activityDuration,
    intl,
    messages,
    travelNights,
}: {
    travelNights?: number | null;
    activityDuration?: number | number[] | null;
    intl: IntlShape;
    messages: ProductTileDurationMessages;
}): ProductTileDurationProps | undefined => {
    const normalizedActivityDuration = normalizeActivityDuration(activityDuration);

    if (travelNights) {
        return {
            durationLabel: getTravelNightsLabel(travelNights, intl, messages),
            durationVariant: DurationVariant.Nights,
        };
    }

    if (normalizedActivityDuration) {
        return {
            durationLabel: getActivityDurationLabel(normalizedActivityDuration, intl, messages),
            durationVariant: DurationVariant.Hours,
        };
    }

    return undefined;
};

export {
    formatSecondsToDuration,
    getActivityDurationLabel,
    getProductTileDuration,
    getTravelNightsLabel,
    type ProductTileDurationMessages,
    type ProductTileDurationProps,
};
